import {
	type Announcements,
	closestCenter,
	DndContext,
	type DragEndEvent,
	type DragOverEvent,
	KeyboardSensor,
	PointerSensor,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import { SortableContext, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { type Dtos } from "@inrev/common";
import { moveWithinBucket, type ProCon } from "@portal/api/decisioning";
import { useState } from "react";
import { Text } from "../../../../primitives";
import { PRO_CON_IMPORTANCE_LABELS } from "../../decisioningLabels";
import { FactorCard } from "./FactorCard";
import { useFactorsBoard } from "./FactorsBoardContext";
import { ImportanceMeter } from "./ImportanceMeter";

type DropTarget = { id: ProCon["id"]; position: "before" | "after" };

/** Dragging holds the dragged card under the pointer; the drop line, not displacement, shows the target. */
const noDisplacement = () => null;

const SortableFactorCard = ({
	proCon,
	dropIndicator,
	reorderLocked,
}: {
	proCon: ProCon;
	dropIndicator?: DropTarget["position"];
	reorderLocked: boolean;
}) => {
	const { pendingIds } = useFactorsBoard();
	const { setNodeRef, transform, transition, attributes, listeners, isDragging } = useSortable({
		id: proCon.id,
		disabled: reorderLocked || pendingIds.has(proCon.id),
	});

	return (
		<div
			ref={setNodeRef}
			style={{ transform: CSS.Transform.toString(transform), transition }}
			data-sortable-id={proCon.id}
			className={isDragging ? "relative z-20" : undefined}
		>
			<FactorCard
				proCon={proCon}
				dragHandleProps={{ ...attributes, ...listeners }}
				dragDisabled={reorderLocked}
				isDragging={isDragging}
				dropIndicator={dropIndicator}
			/>
		</div>
	);
};

export type ImportanceBucketProps = {
	kind: Dtos.Decisioning.ProConKind;
	importance: Dtos.Decisioning.ProConImportance;
	items: readonly ProCon[];
};

/** One importance level of one side. Cards reorder only within it: each bucket has its own drag context. */
export const ImportanceBucket = ({ kind, importance, items }: ImportanceBucketProps) => {
	const { reorder, pendingIds } = useFactorsBoard();
	const [dropTarget, setDropTarget] = useState<DropTarget | null>(null);
	const sensors = useSensors(
		useSensor(PointerSensor),
		useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
	);
	const ids = items.map((item) => item.id);
	const reorderLocked = items.some((item) => pendingIds.has(item.id));

	const nameOf = (id: string | number) =>
		items.find((item) => item.id === id)?.factor.name ?? "factor";
	const positionOf = (id: string | number) => {
		const index = ids.findIndex((candidate) => candidate === id);
		return `position ${index + 1} of ${ids.length}`;
	};
	const announcements: Announcements = {
		onDragStart: ({ active }) => `Picked up ${nameOf(active.id)}, ${positionOf(active.id)}.`,
		onDragOver: ({ active, over }) => {
			if (!over) return `${nameOf(active.id)} is no longer over a drop position.`;
			if (over.id === active.id) return `${nameOf(active.id)} is at ${positionOf(over.id)}.`;
			return `${nameOf(active.id)} is over ${nameOf(over.id)}, ${positionOf(over.id)}.`;
		},
		onDragEnd: ({ active, over }) =>
			over
				? `Dropped ${nameOf(active.id)} at ${positionOf(over.id)}.`
				: `Dropped ${nameOf(active.id)}. It did not move.`,
		onDragCancel: ({ active }) =>
			`Cancelled. ${nameOf(active.id)} returned to ${positionOf(active.id)}.`,
	};

	const handleDragOver = ({ active, over }: DragOverEvent) => {
		if (!over || active.id === over.id) {
			setDropTarget(null);
			return;
		}
		const fromIndex = ids.findIndex((id) => id === active.id);
		const toIndex = ids.findIndex((id) => id === over.id);
		if (fromIndex === -1 || toIndex === -1) return;
		const target = ids[toIndex];
		if (target) setDropTarget({ id: target, position: fromIndex < toIndex ? "after" : "before" });
	};

	const handleDragEnd = ({ active, over }: DragEndEvent) => {
		setDropTarget(null);
		if (reorderLocked || !over || active.id === over.id) return;
		const fromIndex = ids.findIndex((id) => id === active.id);
		const toIndex = ids.findIndex((id) => id === over.id);
		if (fromIndex === -1 || toIndex === -1) return;
		reorder(kind, importance, moveWithinBucket(ids, fromIndex, toIndex));
	};

	return (
		<section
			aria-label={`${PRO_CON_IMPORTANCE_LABELS[importance]} ${kind === "pro" ? "pros" : "cons"}`}
			data-testid={`importance-bucket-${kind}-${importance}`}
			className="grid grid-cols-[22px_minmax(0,1fr)] gap-x-2"
		>
			<div className="flex flex-col items-center gap-1 pt-1" aria-hidden>
				<ImportanceMeter weight={importance} side={kind} />
				<div className="w-px flex-1 bg-bc-default" />
			</div>
			<div className="flex min-w-0 flex-col gap-1.5 pb-3">
				<Text type="caption" color="secondary" className="font-semibold">
					{PRO_CON_IMPORTANCE_LABELS[importance]}
				</Text>
				<DndContext
					sensors={sensors}
					accessibility={{ announcements }}
					collisionDetection={closestCenter}
					onDragOver={handleDragOver}
					onDragEnd={handleDragEnd}
					onDragCancel={() => setDropTarget(null)}
				>
					<SortableContext items={ids} strategy={noDisplacement}>
						<div className="flex flex-col gap-1.5">
							{items.map((item) => (
								<SortableFactorCard
									key={item.id}
									proCon={item}
									reorderLocked={reorderLocked}
									dropIndicator={dropTarget?.id === item.id ? dropTarget.position : undefined}
								/>
							))}
						</div>
					</SortableContext>
				</DndContext>
			</div>
		</section>
	);
};
